import { useCallback, useMemo } from "react";
import type { HotelRoomCardRatesProps } from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
  type HotelRoomRateMeta,
  type OnSelectOptions,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { RadioGroup } from "@tenant/ui";
import clsx from "clsx";
import { usePackageHotelRoomCardRates } from "./hooks";
import defaultStyles from "./HotelRoomCardRates.module.scss";
import { MealPlansToShow } from "./MealPlansToShow";
const COMPONENT_KEY = CoreHotelsComponent.HotelRoomCardRates;

/**
 * Package version of HotelRoomCardRates - uses package pricing instead of hotel-only pricing
 */
export const PackageHotelRoomCardRates = ({
  rates,
  selectedRate,
  setSelectedRate,
  setSelectedRoom,
  className,
  children,
}: HotelRoomCardRatesProps & PlatformComponentProps) => {
  if (rates == null || selectedRate == null || setSelectedRate == null) {
    throw new Error(
      "RBC HotelRoomCardRates requires rates, selectedRate, and setSelectedRate props."
    );
  }
  const { t } = useI18nContext();
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers } = usePackageHotelRoomCardRates({
    rates,
    selectedRate,
    setSelectedRate,
  });

  const {
    baseRates,
    selectedBaseRate,
    showPointsPricing,
    showMealPlans,
    text,
  } = context;

  const { onSelectBaseRateId, mealPlanRatesForBaseRate } = handlers;

  const createOnSelect = useCallback(
    (rate: HotelRoomRateMeta) => (options?: OnSelectOptions) => {
      setSelectedRate!(rate);
      if (options?.selectRoom && setSelectedRoom != null) {
        setSelectedRoom(rate.product);
      }
    },
    [setSelectedRate, setSelectedRoom]
  );

  const baseRatesRadioGroup = useMemo(() => {
    if (baseRates.length === 0) return null;

    if (baseRates.length === 1) {
      const rate = baseRates[0];
      return (
        <rate.View
          product={rate.product}
          priceDifference={rate.priceDifference}
          isSelected={true}
          onSelect={createOnSelect(rate)}
        />
      );
    }

    const radioOptions = baseRates.map((rate) => ({
      value: rate.key,
      label: (
        <rate.View
          product={rate.product}
          priceDifference={rate.priceDifference}
          isSelected={selectedBaseRate.key === rate.key}
          key={rate.key}
          onSelect={createOnSelect(rate)}
        />
      ),
      id: rate.key,
    }));

    return (
      <RadioGroup
        className={cn("rate-radio-group")}
        options={radioOptions}
        defaultValue={selectedBaseRate.key}
        onSelectValue={onSelectBaseRateId}
      />
    );
  }, [baseRates, cn, onSelectBaseRateId, selectedBaseRate.key, createOnSelect]);

  return (
    <div className={clsx(block, className)}>
      {baseRates.length > 1 && (
        <p className={cn("title")}>
          {t("lodgingShopDetails.chooseCancellationPolicy")}
        </p>
      )}
      <div className={cn("rates", ["cancellation"])}>{baseRatesRadioGroup}</div>
      {showMealPlans && (
        <>
          <p className={cn("title")}>
            {t("lodgingShopDetails.chooseMealPlan")}
          </p>
          <div className={cn("rates", ["meal-plans"])}>
            <MealPlansToShow
              rates={mealPlanRatesForBaseRate}
              cn={cn}
              selectedRate={context.selectedRate}
              setSelectedRate={setSelectedRate}
            />
          </div>
        </>
      )}
      <div className={cn("divider")} />
      <div className={cn("price-container")}>
        <div
          className={cn("total-price")}
          aria-label={t("core-packages.hotels.incrementalChange.ariaLabel")}
        >
          <div className={cn("value")}>
            {showPointsPricing ? text.rewardsText : text.priceText}
          </div>
        </div>
        {showPointsPricing && (
          <p className={cn("helper-text")}>
            {t("rewards.orPrice", { price: text.priceText })}
          </p>
        )}
      </div>
      {children}
    </div>
  );
};
